import {
  Component,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  inject,
  model,
} from "@angular/core";
import { FormularGroupTabContentComponent } from "../tab-content.interface";
import { FormControl } from "@angular/forms";
import { FormularGroupBerichte } from "../../../formular-model/formular-groups/interfaces/FormularGroupBerichte";
import { FormularBerichtBild } from "../../../formular-model/formular-groups/interfaces/FormularBericht";
import { CommonKeys } from "../../../formular-model/common/enums";
import {
  BERICHT_BILD_TYPE,
  BERICHT_TEXT_TYPE,
} from "../../../formular-model/common/formular-group-types";
import { SafeUrl } from "@angular/platform-browser";
import { v4 as uuidv4 } from "uuid";
import { FileSelectEvent, FileUpload } from "primeng/fileupload";

@Component({
  selector: "tab-formular-group-berichte",
  templateUrl: "./berichte.component.html",
  styleUrls: ["./berichte.component.scss"],
  standalone: false,
})
export class FormularGroupTabBerichteComponent
  extends FormularGroupTabContentComponent<FormularGroupBerichte>
  implements AfterViewInit
{
  override formularGroupDataKey = "Berichte";

  private _cdr = inject(ChangeDetectorRef);

  @ViewChild("berichtText") berichtTextField: any;
  public berichtTextCtl: FormControl = new FormControl();

  public berichtImages = model<BildData[]>([]);

  // map uuid <=> image to keep track of items that have to be uploaded once form is saved
  private _uploadsMap = new Map<string, ImageToUpload>();

  @ViewChild("uploader") uploader!: FileUpload;

  onSelect(event: FileSelectEvent) {
    event.currentFiles.forEach((file) => this._handleAddItem(file));
    this.uploader.clear();
  }

  ngAfterViewInit(): void {
    if (this.data[this.formularGroupDataKey][CommonKeys.Values].length > 0) {
      this.data[this.formularGroupDataKey][CommonKeys.Values].forEach(
        (bericht: any) => this.parseBerichtItem(bericht),
      );
    } else {
      const newBericht = {
        [CommonKeys.Type]: BERICHT_TEXT_TYPE,
        [CommonKeys.Value]: "",
        [CommonKeys.Label]: null,
      };

      this.parseBerichtItem(newBericht);
    }

    this._cdr.detectChanges();
  }

  private parseBerichtItem(bericht: any) {
    switch (bericht[CommonKeys.Type]) {
      case BERICHT_TEXT_TYPE:
        this.berichtTextCtl.setValue(bericht[CommonKeys.Value]);
        break;

      case BERICHT_BILD_TYPE:
        this._addBerichtBild(bericht);
        break;
    }
  }

  private _addBerichtBild(data: FormularBerichtBild | BildData) {
    const bild: BildData = {
      ...data,
      imgSrc: `/api/Formular/Formulare/${this.data["FormularID" as keyof FormularGroupBerichte]}/Images/${data["ImageID"]}`,
    };

    this.berichtImages.set([...this.berichtImages(), bild]);
    this._cdr.detectChanges();
  }

  public deleteImage(id: string) {
    this._uploadsMap.delete(id);
    this.berichtImages.set(
      this.berichtImages().filter((img) => img.ImageID !== id),
    );

    this._cdr.detectChanges();
  }

  public async reconstructData(): Promise<{
    res: FormularGroupBerichte;
    imagesToUpload: ImageToUpload[];
  }> {
    const res = {
      [CommonKeys.Type]: this.data[CommonKeys.Type],
      [CommonKeys.ID]: this.data[CommonKeys.ID],
      [CommonKeys.Label]: this.data[CommonKeys.Label],
      [CommonKeys.ShowLabel]: this.data[CommonKeys.ShowLabel],
      Berichte: this.data["Berichte"],
    };

    const values = [
      {
        [CommonKeys.Type]: BERICHT_TEXT_TYPE,
        [CommonKeys.Value]: this.berichtTextCtl.value,
        [CommonKeys.Label]: "Bericht",
      },
    ];

    for (const item of this.berichtImages()) {
      if (item === null) continue;

      values.push({
        [CommonKeys.Type]: BERICHT_BILD_TYPE,
        [CommonKeys.Value]: item.Value,
        [CommonKeys.Label]: item.Label,
        ImageID: item.ImageID,
        Beschreibung: item.Beschreibung,
      } as FormularBerichtBild);
    }

    res["Berichte"][CommonKeys.Values] = values;

    return { res: res, imagesToUpload: Array.from(this._uploadsMap.values()) };
  }

  private async _handleAddItem(file: File) {
    const fid = this.data[
      "FormularID" as keyof FormularGroupBerichte
    ] as string;
    const uuid = uuidv4();
    const url = URL.createObjectURL(file);
    const img = {
      [CommonKeys.Type]: BERICHT_BILD_TYPE,
      [CommonKeys.Value]: null,
      [CommonKeys.Label]: file.name,
      ImageID: uuid,
      Beschreibung: "",
      srcOverride: url,
      imgSrc: null,
    };

    this._addBerichtBild(img);
    this._uploadsMap.set(uuid, {
      file: file,
      id: uuid,
      formularID: fid,
      src: url,
    });
    this._cdr.detectChanges();
  }
}

export interface NewImageData {
  file: File;
  url: SafeUrl;
  desc: string;
}

export interface ImageToUpload {
  file: File;
  id: string;
  formularID: string;
  src: string;
}

export interface BildData {
  ImageID: string;
  Beschreibung: string;
  Value: ArrayBuffer | null;
  $type: string;
  Label: string | null;
  imgSrc: string | null;
  srcOverride?: SafeUrl | string | null;
}
